import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { createFileRoute } from "@tanstack/react-router";
import Title from "../../../components/common/Title";
import { TutorialVideo } from "../../../components/common/TutorialVideo";

const ClinicianHelpPage = () => {
	return (
		<section className="lg-width">
			<Title words="Help for Clinicians" />
			<div className="space-y-8">
				<Tabs defaultValue="diaries" className="w-full">
					<TabsList className="grid grid-cols-4 w-full">
						<TabsTrigger value="diaries">Adding Diaries</TabsTrigger>
						<TabsTrigger value="status">Diary Status</TabsTrigger>
						<TabsTrigger value="reminders">Patient Reminders</TabsTrigger>
						<TabsTrigger value="actions">Diary Actions</TabsTrigger>
					</TabsList>

					<TabsContent value="diaries" className="space-y-4 mt-4">
						<TutorialVideo
							title="Adding Diaries to Patient Watch"
							description="Learn how to create and manage diary templates for your patients"
							videoUrl="https://www.loom.com/embed/08a4317ee3d843b1b506328a3034f810?sid=5a15a02d-7844-4eea-9dfd-6248914877a2"
							steps={[
								{
									title: "Access the New Diary Page",
									description:
										"Go to the PatientWatch platform and navigate to the New Diary page. Switch the diary feature on.",
								},
								{
									title: "Add a New Template",
									description:
										"Click on 'Add New Template' to create a new diary template.",
								},
								{
									title: "Connect to Your Clinic or Hospital",
									description:
										"Ensure you are connected to your specific clinic or hospital.",
								},
								{
									title: "Select Questionnaire Type",
									description:
										"Choose the type of questionnaire you want to use: Standard problems questionnaires or Pain and comments (e.g., VAS score and comments).",
								},
							]}
						/>
					</TabsContent>

					<TabsContent value="status" className="space-y-4 mt-4">
						<TutorialVideo
							title="Managing Patient Pain Scores"
							description="Track and categorize patient pain responses and treatment outcomes"
							videoUrl="https://www.loom.com/embed/f2bc7c77f13e4394987ac9abfb562afb?sid=5d596e8b-be33-478e-b46c-b6f60fa0ed1a"
							steps={[
								{
									title: "Patient Invitation and Status Tracking",
									description:
										"Patients are invited to log their pain scores. Initially, they appear in the 'pending' column. Once they start logging their scores, they move to the 'active' column.",
								},
								{
									title: "Categorizing Patient Pain States",
									description:
										"Each patient's diary is categorized based on their pain response. Categories include: Never Works, Pain Spike, and Sustained Relief.",
								},
							]}
						/>
					</TabsContent>

					<TabsContent value="reminders" className="space-y-4 mt-4">
						<TutorialVideo
							title="Patient Reminder Process"
							description="Learn how to manage patient reminders and follow-ups"
							videoUrl="https://www.loom.com/embed/6006d3ab837747cc8ab375847a6528fa?sid=34339c30-2fff-4ed2-b02b-8a66685449a8"
							steps={[
								{
									title: "Patient Invitation to Log Scores",
									description:
										"Patients are invited to participate in patient watch. They receive text and SMS reminders to log their scores.",
								},
								{
									title: "Handling Late Log Situations",
									description:
										"If patients forget to log their scores, they enter a 'late to log' state.",
								},
								{
									title: "Taking Action to Remind Patients",
									description:
										"To help patients get back on track, click 'Take Action' to remind them. A pre-written note is available for this reminder.",
								},
								{
									title: "Sending the Reminder",
									description:
										"Click 'Send' to dispatch the reminder to the patient. The notification will be recorded in the diary history.",
								},
							]}
						/>
					</TabsContent>

					<TabsContent value="actions" className="space-y-4 mt-4">
						<TutorialVideo
							title="Managing Patient Diary Actions"
							description="Learn how to handle patient diary actions and follow-ups"
							videoUrl="https://www.loom.com/embed/25d33318171f4d17a68951b8a522e57c?sid=3bfb9951-efeb-4857-b71a-71d5084a72ef"
							steps={[
								{
									title: "Identify Patient Status",
									description:
										"Observe the patient's diary for any colored flags indicating issues. Example: Otis Redding has a pain spike.",
								},
								{
									title: "Decide on Follow-Up Actions",
									description:
										"Based on the patient's status, determine the next steps: Book a follow-up appointment or consider discharging the patient.",
								},
								{
									title: "Discharge Process",
									description:
										"If discharging the patient: A message will prompt the patient to stop logging in the diary. A report will be sent to the patient.",
								},
								{
									title: "Review Patient Summary",
									description:
										"Access a summary of the patient's diary activities: Review the chart showing the patient's progress.",
								},
								{
									title: "Finalize Patient Status",
									description:
										"Click 'send' to complete the process: Move the patient to the inactive column. Mark the diary as completed, while retaining access to all information.",
								},
							]}
						/>
					</TabsContent>
				</Tabs>
			</div>
		</section>
	);
};

export const Route = createFileRoute("/_app/help/clinician")({
	component: ClinicianHelpPage,
});
